import { useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, Phone, AlertTriangle, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import FAQSection from "@/components/common/FAQSection";

// Static conditions data (in production, this would come from API)
const conditionsData = {
  "hearing-loss": {
    title: "Hearing Loss",
    category: "hearing_ear",
    description: "Comprehensive evaluation and treatment for all types of hearing impairment from mild to profound.",
    symptoms: [
      "Difficulty understanding speech, especially in noisy environments",
      "Frequently asking people to repeat themselves",
      "Turning up the volume on TV or radio",
      "Withdrawal from conversations",
      "Tinnitus (ringing in the ears)",
    ],
    causes: [
      "Age-related hearing loss (presbycusis)",
      "Noise exposure",
      "Ear infections",
      "Genetic factors",
      "Ototoxic medications",
      "Head trauma",
    ],
    diagnosis: "Dr. Sachdeva conducts comprehensive audiometric testing including pure tone audiometry, speech audiometry, and tympanometry to determine the type and severity of hearing loss.",
    treatments: [
      "Hearing aids for mild to moderate loss",
      "Cochlear implants for severe to profound loss",
      "Medical management for treatable causes",
      "Bone-anchored hearing aids (BAHA)",
      "Auditory rehabilitation",
    ],
    redFlags: [
      "Sudden hearing loss (requires immediate attention)",
      "Hearing loss with dizziness or vertigo",
      "Hearing loss with ear discharge",
      "One-sided hearing loss",
    ],
    faqs: [
      {
        question: "When should I see a doctor for hearing loss?",
        answer: "You should consult an ENT specialist if you notice any change in your hearing, especially sudden hearing loss which is a medical emergency. Early intervention leads to better outcomes."
      },
      {
        question: "Can hearing loss be reversed?",
        answer: "Some types of hearing loss (conductive) can be treated and hearing restored. Sensorineural hearing loss is typically permanent but can be effectively managed with hearing aids or cochlear implants."
      },
      {
        question: "What's the difference between hearing aids and cochlear implants?",
        answer: "Hearing aids amplify sound for those with mild to moderate hearing loss. Cochlear implants are for severe to profound loss where hearing aids don't help - they directly stimulate the auditory nerve."
      }
    ]
  },
  "tinnitus": {
    title: "Tinnitus",
    category: "hearing_ear",
    description: "Expert management of persistent ringing, buzzing, or other sounds in the ears.",
    symptoms: [
      "Ringing, buzzing, or hissing sounds in ears",
      "Sounds that only you can hear",
      "May be constant or intermittent",
      "Can affect one or both ears",
      "May worsen with stress or fatigue",
    ],
    causes: [
      "Hearing loss",
      "Exposure to loud noises",
      "Ear infections",
      "Earwax buildup",
      "Certain medications",
      "TMJ disorders",
    ],
    diagnosis: "Evaluation includes hearing tests, examination of the ear, and assessment of possible underlying causes. Sometimes imaging studies may be needed.",
    treatments: [
      "Treating underlying causes",
      "Sound therapy",
      "Hearing aids with tinnitus maskers",
      "Cognitive behavioral therapy",
      "Lifestyle modifications",
    ],
    redFlags: [
      "Pulsatile tinnitus (sounds like heartbeat)",
      "One-sided tinnitus",
      "Tinnitus with hearing loss",
      "Sudden onset tinnitus",
    ],
    faqs: [
      {
        question: "Can tinnitus be cured?",
        answer: "While there's no universal cure for tinnitus, many people find significant relief through various treatments. The goal is to reduce the perception and impact of tinnitus on daily life."
      },
      {
        question: "Is tinnitus a sign of hearing loss?",
        answer: "Tinnitus often accompanies hearing loss, but not always. It's important to have your hearing evaluated if you experience tinnitus."
      }
    ]
  },
  "vertigo": {
    title: "Vertigo & Balance Disorders",
    category: "hearing_ear",
    description: "Specialized care for dizziness, vertigo, and balance problems.",
    symptoms: [
      "Spinning sensation",
      "Loss of balance",
      "Nausea and vomiting",
      "Difficulty walking straight",
      "Abnormal eye movements (nystagmus)",
    ],
    causes: [
      "BPPV (Benign Paroxysmal Positional Vertigo)",
      "Meniere's disease",
      "Vestibular neuritis",
      "Labyrinthitis",
      "Head injury",
    ],
    diagnosis: "Dr. Sachdeva performs detailed vestibular examination including positional testing, balance assessment, and may order specialized tests like VNG or MRI if needed.",
    treatments: [
      "Epley maneuver for BPPV",
      "Vestibular rehabilitation therapy",
      "Medications for acute symptoms",
      "Treatment of underlying conditions",
      "Surgical options for refractory cases",
    ],
    redFlags: [
      "Vertigo with severe headache",
      "Vertigo with weakness or numbness",
      "Vertigo with difficulty speaking",
      "Vertigo with hearing loss",
    ],
    faqs: [
      {
        question: "What is the most common cause of vertigo?",
        answer: "BPPV (Benign Paroxysmal Positional Vertigo) is the most common cause, accounting for about 50% of vertigo cases. It's highly treatable with repositioning maneuvers."
      },
      {
        question: "How long does vertigo last?",
        answer: "Duration varies by cause. BPPV episodes last seconds to minutes, while Meniere's attacks can last hours. Most vertigo conditions are treatable."
      }
    ]
  },
  "sinusitis": {
    title: "Sinusitis",
    category: "nose_sinus",
    description: "Expert treatment for acute and chronic sinus infections.",
    symptoms: [
      "Facial pain and pressure",
      "Nasal congestion",
      "Thick nasal discharge",
      "Reduced sense of smell",
      "Headache",
      "Post-nasal drip",
    ],
    causes: [
      "Viral infections (common cold)",
      "Bacterial infections",
      "Allergies",
      "Nasal polyps",
      "Deviated septum",
    ],
    diagnosis: "Examination includes nasal endoscopy and CT scan of sinuses for chronic cases. Allergy testing may be recommended.",
    treatments: [
      "Medical management with antibiotics and steroids",
      "Nasal irrigation",
      "Allergy management",
      "Functional Endoscopic Sinus Surgery (FESS)",
      "Balloon sinuplasty",
    ],
    redFlags: [
      "High fever with sinus symptoms",
      "Severe headache",
      "Eye swelling or vision changes",
      "Symptoms lasting more than 10 days",
    ],
    faqs: []
  },
  "snoring": {
    title: "Snoring & Sleep Apnea",
    category: "throat_voice",
    description: "Comprehensive solutions for snoring and obstructive sleep apnea.",
    symptoms: [
      "Loud snoring",
      "Witnessed breathing pauses during sleep",
      "Excessive daytime sleepiness",
      "Morning headaches",
      "Difficulty concentrating",
    ],
    causes: [
      "Obesity",
      "Enlarged tonsils or adenoids",
      "Deviated septum",
      "Nasal polyps",
      "Long soft palate or uvula",
    ],
    diagnosis: "Evaluation includes sleep study (polysomnography), nasal endoscopy, and assessment of upper airway anatomy.",
    treatments: [
      "Lifestyle modifications (weight loss)",
      "CPAP therapy",
      "Oral appliances",
      "Surgical options (UPPP, septoplasty)",
      "Robotic surgery for snoring",
    ],
    redFlags: [
      "Witnessed apnea episodes",
      "Severe daytime sleepiness affecting work/driving",
      "Morning headaches",
      "High blood pressure",
    ],
    faqs: []
  }
};

export default function ConditionPage() {
  const { slug } = useParams();
  const condition = conditionsData[slug];

  if (!condition) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-4">Condition not found</h1>
          <Link to="/services">
            <Button>View All Services</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid={`condition-page-${slug}`}>
      {/* Breadcrumb */}
      <div className="bg-slate-50 py-4">
        <div className="container-width">
          <Link to="/services" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-sky-600">
            <ArrowLeft className="w-4 h-4" />
            Back to Services
          </Link>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-white py-12 md:py-16 border-b">
        <div className="container-width">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              {condition.title}
            </h1>
            <p className="text-lg text-slate-600">
              {condition.description}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="section-padding bg-slate-50">
        <div className="container-width">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Symptoms */}
              <div className="bg-white rounded-xl p-8 shadow-sm">
                <h2 className="text-xl font-bold text-slate-900 mb-4">Symptoms</h2>
                <ul className="space-y-3">
                  {condition.symptoms.map((symptom, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-sky-600 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-600">{symptom}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Causes */}
              <div className="bg-white rounded-xl p-8 shadow-sm">
                <h2 className="text-xl font-bold text-slate-900 mb-4">Common Causes</h2>
                <ul className="space-y-3">
                  {condition.causes.map((cause, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="w-2 h-2 rounded-full bg-sky-600 mt-2 flex-shrink-0" />
                      <span className="text-slate-600">{cause}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Diagnosis */}
              <div className="bg-white rounded-xl p-8 shadow-sm">
                <h2 className="text-xl font-bold text-slate-900 mb-4">Diagnosis</h2>
                <p className="text-slate-600">{condition.diagnosis}</p>
              </div>

              {/* Treatments */}
              <div className="bg-white rounded-xl p-8 shadow-sm">
                <h2 className="text-xl font-bold text-slate-900 mb-4">Treatment Options</h2>
                <ul className="space-y-3">
                  {condition.treatments.map((treatment, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-600">{treatment}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Red Flags */}
              {condition.redFlags.length > 0 && (
                <div className="bg-red-50 rounded-xl p-8">
                  <h2 className="text-xl font-bold text-red-900 mb-4 flex items-center gap-2">
                    <AlertTriangle className="w-6 h-6" />
                    When to Seek Immediate Care
                  </h2>
                  <ul className="space-y-3">
                    {condition.redFlags.map((flag, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <span className="w-2 h-2 rounded-full bg-red-600 mt-2 flex-shrink-0" />
                        <span className="text-red-800">{flag}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                {/* CTA Card */}
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <h3 className="font-bold text-slate-900 mb-4">Get Expert Care</h3>
                  <p className="text-sm text-slate-600 mb-4">
                    Consult with Dr. Sanjay Sachdeva for personalized treatment.
                  </p>
                  <div className="space-y-3">
                    <Link to="/book-appointment">
                      <Button className="w-full bg-amber-600 hover:bg-amber-700">
                        <Calendar className="w-4 h-4 mr-2" />
                        Book Appointment
                      </Button>
                    </Link>
                    <a href="tel:+919910317286">
                      <Button variant="outline" className="w-full">
                        <Phone className="w-4 h-4 mr-2" />
                        Call Now
                      </Button>
                    </a>
                  </div>
                </div>

                {/* Related Link */}
                {condition.category === "hearing_ear" && (
                  <div className="bg-sky-50 rounded-xl p-6">
                    <h3 className="font-bold text-slate-900 mb-2">Consider Cochlear Implant?</h3>
                    <p className="text-sm text-slate-600 mb-4">
                      If hearing aids aren't enough, cochlear implants may help.
                    </p>
                    <Link to="/cochlear-implant">
                      <Button variant="outline" className="w-full border-sky-600 text-sky-600">
                        Learn About CI
                      </Button>
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      {condition.faqs.length > 0 && (
        <FAQSection faqs={condition.faqs} title={`${condition.title} FAQs`} />
      )}

      {/* CTA Section */}
      <section className="bg-slate-900 py-12">
        <div className="container-width text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Need Help with {condition.title}?</h2>
          <p className="text-slate-300 mb-6">Book a consultation with Dr. Sachdeva today.</p>
          <Link to="/book-appointment">
            <Button size="lg" className="bg-amber-600 hover:bg-amber-700">
              Book Appointment
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
